import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Search, X, Star, Plus, Trash2 } from "lucide-react";
import { getProcesses, getPortalUsers, getSavedViews, createSavedView, deleteSavedView } from "@/lib/portalApi";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { PROCESS_STATUSES, formatDateTime } from "@/components/portal/portalConstants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const selectClasses =
  "h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-venka-green-light/40";

export const PortalProcesses = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    country_destination: "",
    customer_name: "",
    assigned_agent_id: "",
    office: "",
    team: "",
  });

  useEffect(() => {
    getPortalUsers("agent").then(setAgents).catch(() => {});
    getSavedViews().then(setViews).catch(() => {});
  }, []);

  const saveView = async () => {
    const name = window.prompt("Name this view (e.g. My Luanda immigration queue)");
    if (!name || !name.trim()) return;
    try {
      const v = await createSavedView(name.trim(), filters);
      setViews((prev) => [...prev, v]);
    } catch (e) {
      /* handled globally */
    }
  };

  const removeView = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteSavedView(id);
      setViews((prev) => prev.filter((v) => v.id !== id));
    } catch (e2) {
      /* ignore */
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const data = await getProcesses(params);
      setRows(data);
    } catch (e) {
      // handled globally
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(fetchData, 300);
    return () => clearTimeout(id);
  }, [fetchData]);

  const update = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const clearFilters = () =>
    setFilters({ status: "", country_destination: "", customer_name: "", assigned_agent_id: "", office: "", team: "" });

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-6" data-testid="portal-processes">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("portal.nav.processes")}</h1>
          <p className="text-sm text-slate-400">{t("portal.filters.title")}</p>
        </div>
      </div>

      {/* Saved views */}
      <div className="flex flex-wrap items-center gap-2" data-testid="saved-views">
        <span className="text-xs text-slate-400 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-venka-green-light" /> Saved views:</span>
        {views.length === 0 && <span className="text-xs text-slate-600">none yet</span>}
        {views.map((v) => (
          <span
            key={v.id}
            onClick={() => setFilters({ status: "", country_destination: "", customer_name: "", assigned_agent_id: "", office: "", team: "", ...(v.filters || {}) })}
            className="group inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200 cursor-pointer hover:border-venka-green-light/50"
            data-testid={`saved-view-${v.id}`}
          >
            {v.name}
            <Trash2 className="h-3 w-3 text-slate-500 hover:text-red-400" onClick={(e) => removeView(v.id, e)} data-testid={`saved-view-delete-${v.id}`} />
          </span>
        ))}
        <Button variant="ghost" size="sm" onClick={saveView} className="h-7 text-venka-green-light hover:text-white" data-testid="saved-view-add" disabled={!hasFilters}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Save current
        </Button>
      </div>

      {/* Filters */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t("portal.filters.status")}</label>
          <select
            className={selectClasses}
            value={filters.status}
            onChange={(e) => update("status", e.target.value)}
            data-testid="filter-status"
          >
            <option value="">{t("portal.filters.allStatuses")}</option>
            {PROCESS_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`portal.status.${s}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t("portal.filters.country")}</label>
          <Input
            value={filters.country_destination}
            onChange={(e) => update("country_destination", e.target.value)}
            placeholder={t("portal.filters.search")}
            className="bg-slate-950 border-slate-700"
            data-testid="filter-country"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t("portal.filters.customer")}</label>
          <Input
            value={filters.customer_name}
            onChange={(e) => update("customer_name", e.target.value)}
            placeholder={t("portal.filters.search")}
            className="bg-slate-950 border-slate-700"
            data-testid="filter-customer"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t("portal.filters.agent")}</label>
          <select
            className={selectClasses}
            value={filters.assigned_agent_id}
            onChange={(e) => update("assigned_agent_id", e.target.value)}
            data-testid="filter-agent"
          >
            <option value="">{t("portal.filters.allAgents")}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t("portal.filters.office", "Office")}</label>
          <select
            className={selectClasses}
            value={filters.office}
            onChange={(e) => update("office", e.target.value)}
            data-testid="filter-office"
          >
            <option value="">{t("portal.filters.allOffices", "All offices")}</option>
            <option value="luanda">Luanda</option>
            <option value="tallinn">Tallinn</option>
            <option value="lisbon">Lisbon</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1 block">{t("portal.filters.team", "Team")}</label>
          <select
            className={selectClasses}
            value={filters.team}
            onChange={(e) => update("team", e.target.value)}
            data-testid="filter-team"
          >
            <option value="">{t("portal.filters.allTeams", "All teams")}</option>
            <option value="immigration">Immigration</option>
          </select>
        </div>
        {hasFilters && (
          <div className="sm:col-span-2 lg:col-span-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-300 hover:text-white"
              data-testid="filter-clear"
            >
              <X className="h-3.5 w-3.5 mr-1" /> {t("portal.filters.clear")}
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/70 text-slate-400">
              <tr>
                <th className="text-left font-medium px-4 py-3">{t("portal.table.customer")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.table.type")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.table.destination")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.filters.office", "Office")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.table.status")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.table.agent")}</th>
                <th className="text-left font-medium px-4 py-3">{t("portal.table.updated")}</th>
                <th className="text-right font-medium px-4 py-3">{t("portal.table.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline" />
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="processes-empty">
                    {t("portal.table.empty")}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-slate-800/40 cursor-pointer"
                    onClick={() => navigate(`/portal/processes/${p.id}`)}
                    data-testid={`process-row-${p.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.customer_name || "—"}</div>
                      <div className="text-xs text-slate-500">{p.customer_email || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{p.process_type_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-300">{p.country_destination || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 capitalize">{p.office || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-slate-300">
                      {p.agent_name || <span className="text-slate-500">{t("portal.table.unassigned")}</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                      {formatDateTime(p.updated_at, i18n.resolvedLanguage)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-venka-green-light text-xs font-medium">{t("portal.table.view")}</span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PortalProcesses;
